import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  Grid,
  Switch,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StorageIcon from '@mui/icons-material/Storage';
import axios from 'axios';
import { logDataConnectionError } from '../utils/dataConnectionErrors';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DataTable from './DataTable';
import ChartEditDialog, { detectFilterCandidates } from './ChartEditDialog';
import QueryFolderSidebar from './QueryFolderSidebar';
import { useNotification } from '../contexts/NotificationContext';
import { useMutation } from '@tanstack/react-query';

const QueryEditor = ({ initialQuery = '', initialQueryId = null, initialChart = null, initialQueryDataForChart = null, onQueryExecute, onUpdateChart, onCloseAfterChartSave, selectedDatabase, availableDatabases = [], selectedConnectionId = '', dataConnections = [], onConnectionChange, savedQueries = [], onQueriesChange, onDatabaseChange, onAddChart, onSaveQuery, isAuthenticated = false, authToken = null, managerId = null, adminId = null, userId = null, timezone = 'America/Mexico_City', dynamicFilterOptionSources = [] }) => {
  const { showError, showWarning } = useNotification();
  // Helper para obtener headers de autenticación
  const getAuthHeaders = () => {
    const headers = {
      'Authorization': authToken
    };
    if (adminId) {
      headers['X-Admin-Id'] = adminId;
    } else if (managerId) {
      headers['X-Manager-Id'] = managerId;
    }
    return headers;
  };

  // Cargar query completa bajo demanda (chat + queryData) para no tener todas en memoria
  const fetchFullQueryMutation = useMutation({
    mutationFn: async (queryId) => {
      const res = await axios.get(`/api/queries/${queryId}`, { headers: getAuthHeaders() });
      return res.data?.success ? res.data.query : null;
    },
  });
  const fetchFullQuery = async (queryId) => {
    if (!authToken || (!managerId && !adminId)) return null;
    try {
      return await fetchFullQueryMutation.mutateAsync(queryId);
    } catch (err) {
      console.error('Error al cargar query completa:', err);
      return null;
    }
  };

  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionErrorDetail, setConnectionErrorDetail] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [previewType, setPreviewType] = useState('table');
  const [previewXColumn, setPreviewXColumn] = useState('');
  const [previewYColumn, setPreviewYColumn] = useState('');
  const [previewGroupByColumn, setPreviewGroupByColumn] = useState(''); // Columna para agrupar y sumar
  const [previewLabelColumn, setPreviewLabelColumn] = useState(''); // Columna para leyenda (colores)
  const [previewStacked, setPreviewStacked] = useState(false); // Apilado para barras
  const [previewAdvancedConfig, setPreviewAdvancedConfig] = useState(null);
  const [previewHeatmapGridConfig, setPreviewHeatmapGridConfig] = useState({ gridRows: 3, gridCols: 3, showValues: true, valueColumn: '', filterColumn: '', cellAssignments: {} });
  const [previewMapConfig, setPreviewMapConfig] = useState({ latColumn: '', lngColumn: '', addressColumn: '', comunaColumn: '', regionColumn: '', countryColumn: '', labelColumn: '', valueColumn: '', valueMapping: 'size', valueColorMin: '#2196f3', valueColorMax: '#f44336', popupColumns: [], markerStyle: 'circle', markerColor: '#1976d2', markerSize: 'medium' });
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editingQueryId, setEditingQueryId] = useState(null);
  const [editName, setEditName] = useState('');
  const [examplesMenuAnchor, setExamplesMenuAnchor] = useState(null);
  const [currentDatabase, setCurrentDatabase] = useState(selectedDatabase);
  const [currentConnectionId, setCurrentConnectionId] = useState(selectedConnectionId || '');
  const [queryAvailableDatabases, setQueryAvailableDatabases] = useState([]);
  const [loadingQueryDatabases, setLoadingQueryDatabases] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveHintParamQuery, setSaveHintParamQuery] = useState(false);
  const [lastReplacedLiteral, setLastReplacedLiteral] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Generación SQL con IA deprecada; pestañas: 0 = Query SQL, 1 = Filtros
  const [sqlTabValue, setSqlTabValue] = useState(0);
  const [chatMessages, setChatMessages] = useState([]); // Historial de conversación del chat - fuente de verdad
  const [compareResults, setCompareResults] = useState(false); // Checkbox para comparar resultados
  const [previousQueryResult, setPreviousQueryResult] = useState(null); // Resultado anterior para comparar
  const [loadingFullQuery, setLoadingFullQuery] = useState(false); // Cargando query completa desde API
  const [suggestedFiltersFromAi, setSuggestedFiltersFromAi] = useState([]); // Filtros sugeridos (persistidos con la query)
  const [previewFilterValues, setPreviewFilterValues] = useState({}); // Valores por defecto de filtros para previsualización
  const [queryDynamicFilterConfig, setQueryDynamicFilterConfig] = useState({
    enabled: false,
    paramName: '',
    filterType: 'select',
    optionSource: 'query',
    optionsQuery: '',
    labelColumn: '',
    valueColumn: '',
    staticOptions: [],
    selectedValue: '',
    allowExecuteWithoutSelection: false,
  });
  const [filterCandidates, setFilterCandidates] = useState([]);
  const textareaRef = useRef(null);
  const chatEndRef = useRef(null);
  const aiPromptInputRef = useRef(null);
  const [dbSchemaTables, setDbSchemaTables] = useState([]);
  const [aiHashSuggestions, setAiHashSuggestions] = useState({
    open: false,
    items: [],
    replaceFrom: 0,
    replaceTo: 0,
    x: 0,
    y: 0,
  });

  // Autocompletar #Tabla.Campo desde catálogo activo (sin JSON manual)
  useEffect(() => {
    if (!authToken || (!managerId && !adminId)) return;
    const headers = { Authorization: authToken };
    if (adminId) headers['X-Admin-Id'] = adminId;
    else if (managerId) headers['X-Manager-Id'] = managerId;
    const connId = currentConnectionId || selectedConnectionId;
    const dbName = currentDatabase || selectedDatabase;
    if (!connId || !dbName) return;
    axios
      .get('/api/catalog/autocomplete', {
        headers,
        params: { connectionId: connId, database: dbName },
      })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.tables) && res.data.tables.length > 0) {
          setDbSchemaTables(res.data.tables);
        }
      })
      .catch(() => {
        // Silencioso: sin catálogo activo no hay autocompletar estructurado
      });
  }, [authToken, managerId, adminId, currentConnectionId, selectedConnectionId, currentDatabase, selectedDatabase]);

  useEffect(() => {
    if (selectedConnectionId && selectedConnectionId !== currentConnectionId) {
      setCurrentConnectionId(selectedConnectionId);
    }
  }, [selectedConnectionId]);

  useEffect(() => {
    if (!currentConnectionId && dataConnections.length > 0) {
      const def = dataConnections.find((c) => c.isDefault) || dataConnections[0];
      if (def?.id) setCurrentConnectionId(def.id);
    }
  }, [dataConnections, currentConnectionId]);

  const pickDatabaseForConnection = (connectionId, databases, { preferred, keepCurrent, current } = {}) => {
    const conn = dataConnections.find((c) => c.id === connectionId);
    const configured = conn?.database?.trim();
    const list = Array.isArray(databases) ? databases : [];

    const findInList = (name) => {
      if (!name) return '';
      const trimmed = String(name).trim();
      return list.find((db) => db === trimmed || db.toLowerCase() === trimmed.toLowerCase()) || '';
    };

    if (preferred?.trim()) {
      return findInList(preferred) || String(preferred).trim();
    }

    if (keepCurrent && current?.trim()) {
      const kept = findInList(current);
      if (kept) return kept;
      if (!list.length) return String(current).trim();
    }

    if (configured) {
      const fromList = findInList(configured);
      if (fromList) return fromList;
      return configured;
    }

    if (list.length === 1) return list[0];
    return '';
  };

  const loadDatabasesForConnectionMutation = useMutation({
    mutationFn: (connectionId) =>
      axios.get('/api/databases', { headers: getAuthHeaders(), params: { connectionId } }),
  });

  const loadDatabasesForConnection = async (connectionId, options = {}) => {
    const { preferredDatabase, keepCurrent = false } = options;
    if (!connectionId || !authToken || (!managerId && !adminId)) {
      setQueryAvailableDatabases([]);
      return;
    }
    setLoadingQueryDatabases(true);
    try {
      const res = await loadDatabasesForConnectionMutation.mutateAsync(connectionId);
      if (res.data.success) {
        const dbs = res.data.databases || [];
        setQueryAvailableDatabases(dbs);
        setCurrentDatabase((prev) => {
          const picked = pickDatabaseForConnection(connectionId, dbs, {
            preferred: preferredDatabase,
            keepCurrent,
            current: prev,
          });
          return picked || (keepCurrent ? prev : '');
        });
      }
    } catch (err) {
      if (!logDataConnectionError('QueryEditor: bases de datos', err)) {
        console.warn('No se pudieron cargar BDs para la conexión:', err);
      }
      setQueryAvailableDatabases([]);
      const conn = dataConnections.find((c) => c.id === connectionId);
      if (conn?.database?.trim() && !keepCurrent) {
        setCurrentDatabase(conn.database.trim());
      }
    } finally {
      setLoadingQueryDatabases(false);
    }
  };

  useEffect(() => {
    if (currentConnectionId && dataConnections.length > 0) {
      loadDatabasesForConnection(currentConnectionId, { keepCurrent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConnectionId, dataConnections, authToken, managerId, adminId]);

  const activeConnection = dataConnections.find((c) => c.id === currentConnectionId);
  const databasesForSelect = useMemo(() => {
    let list = queryAvailableDatabases.length > 0 ? queryAvailableDatabases : availableDatabases;
    const configured = activeConnection?.database?.trim();
    if (configured && !list.some((db) => db.toLowerCase() === configured.toLowerCase())) {
      list = [configured, ...list];
    }
    // Si ya hay una BD seleccionada (ej. cargada desde una query guardada) pero
    // la lista para esta conexión todavía no la incluye (aún cargando, o la
    // conexión cambió), agregarla igual -- si no, el Select queda con un value
    // que no matchea ningún MenuItem y MUI tira "out-of-range value".
    const current = currentDatabase?.trim();
    if (current && !list.some((db) => db.toLowerCase() === current.toLowerCase())) {
      list = [current, ...list];
    }
    return list;
  }, [queryAvailableDatabases, availableDatabases, activeConnection, currentDatabase]);

  const handleQueryConnectionChange = (connId) => {
    setCurrentConnectionId(connId);
    loadDatabasesForConnection(connId);
  };

  const handleQueryDatabaseChange = (db) => {
    setCurrentDatabase(db);
  };

  const resetQueryDataSource = () => {
    const def = dataConnections.find((c) => c.isDefault) || dataConnections[0];
    if (def?.id) {
      setCurrentConnectionId(def.id);
      loadDatabasesForConnection(def.id);
    } else {
      setCurrentDatabase('');
    }
  };

  const handleNewQuery = () => {
    setSelectedQueryId(null);
    setQuery('');
    setQueryResult(null);
    setLastReplacedLiteral(null);
    setAiPrompt('');
    setChatMessages([]);
    setPreviousQueryResult(null);
    setSqlTabValue(0);
    setSuggestedFiltersFromAi([]);
    setPreviewFilterValues({});
    resetQueryDataSource();
    setQueryDynamicFilterConfig({
      enabled: false,
      paramName: '',
      filterType: 'select',
      optionSource: 'query',
      optionsQuery: '',
      labelColumn: '',
      valueColumn: '',
      staticOptions: [],
      selectedValue: '',
      allowExecuteWithoutSelection: false,
    });
    setError(null);
  };

  const isNewQueryDraft = selectedQueryId == null;

  const updateAiHashtagSuggestions = (value, cursorPos) => {
    if (!dbSchemaTables || dbSchemaTables.length === 0) {
      if (aiHashSuggestions.open) setAiHashSuggestions((s) => ({ ...s, open: false }));
      return;
    }
    const text = value || '';
    const pos = typeof cursorPos === 'number' ? cursorPos : text.length;
    const left = text.slice(0, pos);
    const hashIndex = left.lastIndexOf('#');
    if (hashIndex === -1) {
      if (aiHashSuggestions.open) setAiHashSuggestions((s) => ({ ...s, open: false }));
      return;
    }
    // No considerar si hay espacio o salto de línea entre # y el cursor
    const between = left.slice(hashIndex + 1);
    if (/\s/.test(between)) {
      if (aiHashSuggestions.open) setAiHashSuggestions((s) => ({ ...s, open: false }));
      return;
    }
    const token = between; // sin '#'
    const dotIndex = token.indexOf('.');
    const items = [];

    // Calcular una X aproximada relativa al inicio de la línea actual
    const lastNl = left.lastIndexOf('\n');
    const colInLine = lastNl === -1 ? left.length : left.length - lastNl - 1;
    const approxCharWidth = 7; // px, aproximado para monospace/IA input
    const xOffset = Math.max(0, colInLine * approxCharWidth);

    // Posición absoluta del popover a partir del TextField (debajo del input, alineado al cursor aprox.)
    let popupX = 0;
    let popupY = 0;
    if (aiPromptInputRef.current) {
      const rect = aiPromptInputRef.current.getBoundingClientRect();
      popupX = rect.left + 16 + xOffset; // 16px aprox. de padding interno
      popupY = rect.bottom + 4;
    }

    if (dotIndex === -1) {
      // Autocompletar tablas: #TABLA
      const prefix = token.toUpperCase();
      dbSchemaTables.forEach((t) => {
        const name = String(t.table_name || '').toUpperCase();
        if (!prefix || name.includes(prefix)) {
          items.push({
            kind: 'table',
            insert: t.table_name,
            primary: t.table_name,
            secondary: t.description || '',
          });
        }
      });
    } else {
      // Autocompletar columnas: #TABLA.CAMPO
      const tablePart = token.slice(0, dotIndex);
      const colPart = token.slice(dotIndex + 1).toUpperCase();
      const table = dbSchemaTables.find(
        (t) => String(t.table_name || '').toUpperCase().startsWith(tablePart.toUpperCase())
      );
      if (table && Array.isArray(table.fields)) {
        table.fields.forEach((f) => {
          const code = String(f.code || '').toUpperCase();
          if (!colPart || code.includes(colPart)) {
            const base = `${table.table_name}.${f.code}`;
            items.push({
              kind: 'column',
              insert: base,
              primary: base,
              secondary: f.detail || '',
            });
          }
        });
      }
    }

    if (items.length === 0) {
      if (aiHashSuggestions.open) setAiHashSuggestions((s) => ({ ...s, open: false }));
      return;
    }

    setAiHashSuggestions({
      open: true,
      items,
      replaceFrom: hashIndex,
      replaceTo: pos,
      x: popupX,
      y: popupY,
    });
  };

  // Aplicar datos de una query completa al estado (usado tras fetchFullQuery)
  const applyFullQueryToState = (fullQuery, options = {}) => {
    if (!fullQuery) return;
    const { skipDatabase, skipPreviewColumns } = options;
    if (fullQuery.connectionId) {
      setCurrentConnectionId(fullQuery.connectionId);
      loadDatabasesForConnection(fullQuery.connectionId, {
        preferredDatabase: skipDatabase ? undefined : fullQuery.database,
        keepCurrent: false,
      });
    } else if (fullQuery.database && !skipDatabase) {
      setCurrentDatabase(fullQuery.database);
      if (onDatabaseChange) onDatabaseChange(fullQuery.database);
    }
    setSelectedQueryId(fullQuery.id);
    // La query mostrada es la guardada (fullQuery.query), no la del último mensaje del chat, para que reemplazos como @param se conserven al recargar
    setQuery(fullQuery.query || '');
    setLastReplacedLiteral(null);
    if (fullQuery.chatHistory && Array.isArray(fullQuery.chatHistory) && fullQuery.chatHistory.length > 0) {
      setChatMessages(fullQuery.chatHistory);
    } else {
      setChatMessages([]);
    }
    setQueryResult(fullQuery.queryData || null);
    setSqlTabValue(0);
    // Restaurar configuración de filtro dinámico a nivel de query
    if (fullQuery.dynamicFilterConfig && typeof fullQuery.dynamicFilterConfig === 'object') {
      setQueryDynamicFilterConfig({
        enabled: fullQuery.dynamicFilterConfig.enabled !== false,
        paramName: fullQuery.dynamicFilterConfig.paramName || '',
        filterType: fullQuery.dynamicFilterConfig.filterType || 'select',
        optionSource: fullQuery.dynamicFilterConfig.optionSource || 'query',
        optionsQuery: fullQuery.dynamicFilterConfig.optionsQuery || '',
        labelColumn: fullQuery.dynamicFilterConfig.labelColumn || '',
        valueColumn: fullQuery.dynamicFilterConfig.valueColumn || '',
        staticOptions: Array.isArray(fullQuery.dynamicFilterConfig.staticOptions) ? fullQuery.dynamicFilterConfig.staticOptions : [],
        selectedValue: fullQuery.dynamicFilterConfig.selectedValue ?? '',
        allowExecuteWithoutSelection: fullQuery.dynamicFilterConfig.allowExecuteWithoutSelection === true,
      });
    } else {
      setQueryDynamicFilterConfig({
        enabled: false,
        paramName: '',
        filterType: 'select',
        optionSource: 'query',
        optionsQuery: '',
        labelColumn: '',
        valueColumn: '',
        staticOptions: [],
        selectedValue: '',
        allowExecuteWithoutSelection: false,
      });
    }
    setSuggestedFiltersFromAi(Array.isArray(fullQuery.suggestedFilters) ? fullQuery.suggestedFilters : []);
    setPreviewFilterValues(fullQuery.suggestedFilterDefaults && typeof fullQuery.suggestedFilterDefaults === 'object' ? fullQuery.suggestedFilterDefaults : {});
    if (!skipPreviewColumns) {
      if (fullQuery.queryData?.columns) {
        const columns = Array.isArray(fullQuery.queryData.columns)
          ? fullQuery.queryData.columns
          : Object.keys(fullQuery.queryData.data?.[0] || {});
        if (columns.length > 0) {
          setPreviewXColumn(columns[0]);
          setPreviewYColumn(columns.length > 1 ? columns[1] : columns[0]);
        }
      }
    }
  };

  // Solo inicializar currentDatabase si no tiene valor y selectedDatabase sí lo tiene
  useEffect(() => {
    if (!currentDatabase && selectedDatabase) {
      setCurrentDatabase(selectedDatabase);
    }
  }, [selectedDatabase]);

  useEffect(() => {
    testConnection();
  }, [currentDatabase]);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  // Sincronizar selectedQueryId con initialQueryId de inmediato (para que Guardar actualice y no cree nueva si se guarda antes de cargar)
  useEffect(() => {
    setSelectedQueryId(initialQueryId ?? null);
  }, [initialQueryId]);

  // Cargar query completa cuando se abre el editor con initialQueryId (lista es ligera, sin queryData/chatHistory)
  useEffect(() => {
    if (!initialQueryId || !authToken || (!managerId && !adminId)) return;
    let cancelled = false;
    const load = async () => {
      setLoadingFullQuery(true);
      try {
        const fullQuery = await fetchFullQuery(initialQueryId);
        if (cancelled) return;
        if (fullQuery) {
          applyFullQueryToState(fullQuery, { skipPreviewColumns: !!initialChart });
          if (initialChart) {
            setPreviewType(initialChart.type === 'table' ? 'table' : (initialChart.type || 'line'));
            setPreviewXColumn(initialChart.xColumn || '');
            setPreviewYColumn(initialChart.yColumn || '');
            setPreviewGroupByColumn(initialChart.groupByColumn || '');
            setPreviewLabelColumn(initialChart.labelColumn || '');
            setPreviewStacked(initialChart.stacked || false);
            setPreviewAdvancedConfig(initialChart.advancedConfig || null);
            setPreviewHeatmapGridConfig(initialChart.heatmapGridConfig || { gridRows: 3, gridCols: 3, showValues: true, valueColumn: '', filterColumn: '', cellAssignments: {} });
            setPreviewMapConfig(initialChart.mapConfig || { latColumn: '', lngColumn: '', addressColumn: '', comunaColumn: '', regionColumn: '', countryColumn: '', labelColumn: '', valueColumn: '', valueMapping: 'size', valueColorMin: '#2196f3', valueColorMax: '#f44336', popupColumns: [], markerStyle: 'circle', markerColor: '#1976d2', markerSize: 'medium' });
          }
          if (initialChart && (!fullQuery.queryData || !fullQuery.queryData.data) && initialQueryDataForChart?.data) {
            setQueryResult(initialQueryDataForChart);
          }
        }
      } finally {
        if (!cancelled) setLoadingFullQuery(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [initialQueryId, initialChart, initialQueryDataForChart]);

  // Scroll automático al final del chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, aiLoading]);

  // Validar formato SQL en tiempo real
  // Devuelve null si la query es válida, o el mensaje de error si no lo es.
  // También actualiza validationError (state) para el helperText del campo.
  const validateSQL = (sqlQuery) => {
    if (!sqlQuery || !sqlQuery.trim()) {
      setValidationError(null);
      return null;
    }

    // Remover comentarios para validación
    let cleaned = sqlQuery.trim();
    cleaned = cleaned.replace(/--[^\n\r]*[\n\r]*/g, '');
    cleaned = cleaned.replace(/\/\*[\s\S]*?\*\//g, '');
    cleaned = cleaned.trim();

    if (!cleaned) {
      setValidationError(null);
      return null;
    }

    // Validar que empiece con SELECT o WITH (después de remover comentarios)
    const upperCleaned = cleaned.toUpperCase();
    if (!upperCleaned.startsWith('SELECT') && !upperCleaned.startsWith('WITH')) {
      const msg = 'La query debe comenzar con SELECT o WITH';
      setValidationError(msg);
      return msg;
    }

    // Validar paréntesis balanceados
    const openParens = (cleaned.match(/\(/g) || []).length;
    const closeParens = (cleaned.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      const msg = 'Paréntesis no balanceados';
      setValidationError(msg);
      return msg;
    }

    // Validar comillas balanceadas
    const singleQuotes = (cleaned.match(/'/g) || []).length;
    if (singleQuotes % 2 !== 0) {
      const msg = 'Comillas simples no balanceadas';
      setValidationError(msg);
      return msg;
    }

    const doubleQuotes = (cleaned.match(/"/g) || []).length;
    if (doubleQuotes % 2 !== 0) {
      const msg = 'Comillas dobles no balanceadas';
      setValidationError(msg);
      return msg;
    }

    // Validar palabras clave peligrosas (por seguridad) - comandos de escritura
    // Buscar comandos peligrosos que no sean parte de nombres de columnas o comentarios
    const dangerousKeywords = [
      'DROP', 'DELETE', 'UPDATE', 'INSERT', 'ALTER', 'TRUNCATE',
      'EXEC', 'EXECUTE', 'CREATE', 'REPLACE', 'MERGE', 'GRANT',
      'REVOKE', 'DENY', 'BACKUP', 'RESTORE'
    ];
    for (const keyword of dangerousKeywords) {
      // Buscar la palabra clave como comando SQL
      // Usar word boundary para evitar falsos positivos en nombres de columnas
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (regex.test(cleaned)) {
        const msg = `Comando de escritura no permitido: ${keyword}`;
        setValidationError(msg);
        return msg;
      }
    }

    setValidationError(null);
    return null;
  };

  // Reemplaza en la query el literal por @paramName (para filtro dinámico).
  // Soporta: col = 'valor', LTRIM(RTRIM(col)) = 'valor', col LIKE 'valor', y reemplazo por snippet completo.
  const replaceLiteralWithParam = (queryText, paramName, literalValue, snippet) => {
    if (!queryText) return queryText;
    const paramRef = `@${paramName}`;
    if (literalValue != null && typeof literalValue !== 'object') {
      const escaped = String(literalValue).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Caso simple: (alias.)col = 'valor'
      const simpleEq = new RegExp(`(\\b(?:\\w+\\.)?\\w+\\s*=\\s*)'${escaped}'`, 'gi');
      let out = queryText.replace(simpleEq, `$1${paramRef}`);
      if (out !== queryText) return out;
      // Caso LIKE: (alias.)col LIKE 'valor'
      const simpleLike = new RegExp(`(\\b(?:\\w+\\.)?\\w+\\s+LIKE\\s+)'${escaped}'`, 'gi');
      out = queryText.replace(simpleLike, `$1${paramRef}`);
      if (out !== queryText) return out;
      // Caso LTRIM(RTRIM(...)) = 'valor' o LIKE 'valor': reemplazar por snippet modificado
      if (snippet && typeof snippet === 'string' && queryText.includes(snippet)) {
        const escQuote = escaped.replace(/'/g, "\\'");
        const escDbl = String(literalValue).replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/"/g, '\\"');
        const withParam = snippet
          .replace(new RegExp(`'${escQuote}'`, 'g'), paramRef)
          .replace(new RegExp(`"${escDbl}"`, 'g'), paramRef);
        if (withParam !== snippet) return queryText.replace(snippet, withParam);
      }
    }
    if (snippet && typeof snippet === 'string' && queryText.includes(snippet)) {
      return queryText.replace(snippet, `${paramName} = ${paramRef}`);
    }
    return queryText;
  };

  // Revierte @paramName en la query por el literal (para restaurar al cambiar de filtro dinámico)
  const replaceParamWithLiteral = (queryText, paramName, literalValue) => {
    if (!queryText || paramName == null || literalValue == null) return queryText;
    const paramEscaped = String(paramName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const literalForSql = String(literalValue).replace(/'/g, "''");
    const regex = new RegExp(`@${paramEscaped}\\b`, 'gi');
    return queryText.replace(regex, `'${literalForSql}'`);
  };

  const mergePreviewDynamicFilters = (paramConfigs = []) => {
    if (!Array.isArray(paramConfigs) || paramConfigs.length === 0) return;
    setPreviewAdvancedConfig((prev) => {
      const base = prev && typeof prev === 'object' ? { ...prev } : {};
      const existing = Array.isArray(base.dynamicFilters)
        ? [...base.dynamicFilters]
        : (base.dynamicFilter ? [{ ...base.dynamicFilter }] : []);
      paramConfigs.forEach((cfg) => {
        const p = String(cfg?.paramName || '').trim();
        if (!p) return;
        const idx = existing.findIndex((f) => String(f?.paramName || '').trim() === p);
        const nextCfg = {
          enabled: true,
          filterType: cfg.filterType || 'select',
          paramName: p,
          optionSource: cfg.optionSource || 'query',
          optionsQuery: cfg.optionsQuery || '',
          labelColumn: cfg.labelColumn || '',
          valueColumn: cfg.valueColumn || '',
          staticOptions: Array.isArray(cfg.staticOptions) ? cfg.staticOptions : [],
          selectedValue: cfg.selectedValue ?? '',
          allowExecuteWithoutSelection: cfg.allowExecuteWithoutSelection === true,
        };
        if (idx >= 0) existing[idx] = { ...existing[idx], ...nextCfg };
        else existing.push(nextCfg);
      });
      return { ...base, dynamicFilters: existing, dynamicFilter: existing[0] || base.dynamicFilter };
    });
  };

  const applyDateRangeSuggestionToQuery = (queryText, dateColumnExpr) => {
    if (!queryText || !dateColumnExpr) return queryText;
    const esc = String(dateColumnExpr).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let out = queryText;
    out = out.replace(new RegExp(`(${esc}\\s*>=\\s*)([^\\n\\r]+)`, 'i'), `$1@FechaInicio`);
    out = out.replace(new RegExp(`(${esc}\\s*<\\s*)([^\\n\\r]+)`, 'i'), `$1DATEADD(DAY, 1, @FechaFin)`);
    out = out.replace(new RegExp(`(${esc}\\s*<=\\s*)([^\\n\\r]+)`, 'i'), `$1@FechaFin`);
    if (out !== queryText) return out;
    if (/\bWHERE\b/i.test(queryText)) {
      return queryText.replace(/\bWHERE\b/i, `WHERE ${dateColumnExpr} >= @FechaInicio AND ${dateColumnExpr} < DATEADD(DAY, 1, @FechaFin) AND `);
    }
    return queryText;
  };

  const applyDatePartSuggestionToQuery = (queryText, dateColumnExpr, part) => {
    if (!queryText || !dateColumnExpr || !part) return queryText;
    const upperPart = String(part).toUpperCase();
    const condition = upperPart === 'YEAR'
      ? `YEAR(${dateColumnExpr}) = @Anio`
      : `MONTH(${dateColumnExpr}) = @Mes`;
    if (new RegExp(`${upperPart}\\s*\\(`, 'i').test(queryText) && /@Anio|@Mes/i.test(queryText)) return queryText;
    if (/\bWHERE\b/i.test(queryText)) {
      return queryText.replace(/\bWHERE\b/i, `WHERE ${condition} AND `);
    }
    return queryText;
  };

  const handleReplaceQueryLiteral = (paramName, literalValue, snippet) => {
    const prev = lastReplacedLiteral;
    const isChangeOfValue = prev && prev.paramName === paramName && String(prev.literalValue) !== String(literalValue);

    if (isChangeOfValue) {
      // Cambio de valor: devolver el anterior a la query como literal; el nuevo valor se usa en la próxima ejecución
      const newQuery = replaceParamWithLiteral(query, paramName, prev.literalValue);
      setQuery(newQuery);
      setLastReplacedLiteral({ paramName, literalValue, snippet: prev.snippet || null });
      setChatMessages(prevChat => {
        const idx = [...prevChat].reverse().findIndex(m => m.role === 'assistant' && m.query);
        if (idx === -1) return prevChat;
        const realIdx = prevChat.length - 1 - idx;
        const next = [...prevChat];
        next[realIdx] = { ...next[realIdx], query: newQuery };
        return next;
      });
      return;
    }

    let q = query;
    if (prev) {
      q = replaceParamWithLiteral(q, prev.paramName, prev.literalValue);
    }
    const newQuery = replaceLiteralWithParam(q, paramName, literalValue, snippet);
    setQuery(newQuery);
    setLastReplacedLiteral({ paramName, literalValue, snippet: snippet || null });
    setChatMessages(prevChat => {
      const idx = [...prevChat].reverse().findIndex(m => m.role === 'assistant' && m.query);
      if (idx === -1) return prevChat;
      const realIdx = prevChat.length - 1 - idx;
      const next = [...prevChat];
      next[realIdx] = { ...next[realIdx], query: newQuery };
      return next;
    });
  };

  const testConnectionMutation = useMutation({
    mutationFn: () =>
      axios.get('/api/test-connection', {
        params: {
          connectionId: currentConnectionId || selectedConnectionId || undefined,
          database: currentDatabase,
        },
        headers: getAuthHeaders(),
      }),
  });

  const testConnection = async () => {
    try {
      const response = await testConnectionMutation.mutateAsync();
      if (response.data.success) {
        setConnectionStatus('success');
        setConnectionErrorDetail('');
      } else {
        setConnectionStatus('error');
        setConnectionErrorDetail(response.data.error || '');
      }
    } catch (err) {
      setConnectionStatus('error');
      const detail = err.response?.data?.error || err.message || '';
      const code = err.response?.data?.details?.code;
      setConnectionErrorDetail(detail);
      // ECONNREFUSED en local casi siempre es un contenedor Docker apagado, no un
      // bug (ver README) -- esa pista es solo para desarrollo, no va en el Alert
      // que ve cualquier usuario (en producción no hay Docker que revisar).
      if (code === 'ECONNREFUSED') {
        console.warn('[dev] ECONNREFUSED: revisa que el contenedor de la base de datos esté corriendo (docker ps -a).');
      }
    }
  };

  const queryDataSourceBar = (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        bgcolor: 'rgba(0, 0, 0, 0.25)',
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StorageIcon sx={{ fontSize: 18, color: '#90caf9' }} />
        <Typography variant="caption" sx={{ color: '#b0b0b0', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          Origen de datos de esta query
        </Typography>
        {activeConnection?.engine && (
          <Chip
            size="small"
            label={activeConnection.engine === 'timescaledb' ? 'TimescaleDB' : 'SQL Server'}
            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(144, 202, 249, 0.15)', color: '#90caf9' }}
          />
        )}
        {!currentDatabase && (
          <Chip size="small" label="Elige una BD" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Probar conexión">
          <IconButton
            onClick={testConnection}
            size="small"
            sx={{ color: connectionStatus === 'success' ? '#66bb6a' : '#858585' }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl
          size="small"
          sx={{
            minWidth: 160,
            flex: 1,
            '& .MuiInputLabel-root': { color: '#858585' },
            '& .MuiOutlinedInput-root': { color: '#e0e0e0', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
          }}
          disabled={dataConnections.length === 0}
        >
          <InputLabel>Conexión</InputLabel>
          <Select
            value={currentConnectionId || ''}
            label="Conexión"
            onChange={(e) => handleQueryConnectionChange(e.target.value)}
          >
            {dataConnections.length === 0 ? (
              <MenuItem disabled>Sin conexiones configuradas</MenuItem>
            ) : (
              dataConnections.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name || c.host || c.id}{c.isDefault ? ' ★' : ''}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{
            minWidth: 180,
            flex: 1,
            '& .MuiInputLabel-root': { color: '#858585' },
            '& .MuiOutlinedInput-root': { color: '#e0e0e0', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
          }}
          disabled={!currentConnectionId || loadingQueryDatabases}
        >
          <InputLabel>Base de datos</InputLabel>
          <Select
            value={currentDatabase || ''}
            label="Base de datos"
            onChange={(e) => handleQueryDatabaseChange(e.target.value)}
          >
            {/* Mientras loadingQueryDatabases es true, la rama de abajo NO
                renderiza databasesForSelect (solo el MenuItem de "Cargando…"),
                asi que currentDatabase no aparece como opcion aunque SI este
                en databasesForSelect -- por eso el chequeo de "esta en la
                lista" no alcanza, hay que sumar el estado de carga. */}
            {currentDatabase &&
            (loadingQueryDatabases ||
              !databasesForSelect.some((db) => db.toLowerCase() === currentDatabase.toLowerCase())) ? (
              <MenuItem value={currentDatabase}>{currentDatabase}</MenuItem>
            ) : null}
            {loadingQueryDatabases ? (
              <MenuItem disabled value="">Cargando…</MenuItem>
            ) : databasesForSelect.length === 0 ? (
              <MenuItem disabled value="">
                {currentConnectionId ? 'Sin BDs disponibles' : 'Elige conexión primero'}
              </MenuItem>
            ) : (
              databasesForSelect.map((db) => (
                <MenuItem key={db} value={db}>{db}</MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );

  const executeQueryMutation = useMutation({
    mutationFn: (body) => axios.post('/api/query', body, { headers: getAuthHeaders() }),
  });

  const executeQuery = async (queryOverride = null) => {
    let userQuery = (queryOverride != null ? String(queryOverride) : query).trim();
    if (!userQuery.trim()) {
      // Fallback: usar última query del chat si el editor está vacío
      const lastAssistantMessage = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.query);
      const chatQuery = lastAssistantMessage?.query?.trim();
      if (chatQuery && !validateSQL(chatQuery)) {
        userQuery = chatQuery;
        setQuery(chatQuery);
      } else {
        setError('Por favor ingresa una query SQL');
        return;
      }
    }

    // Si hay filtros dinámicos activos sin selección requerida, no ejecutar.
    const missingRequiredFilters = activeDynamicFiltersForPreview.filter(
      (f) => f.allowExecuteWithoutSelection !== true && (f.selectedValue == null || String(f.selectedValue).trim() === '')
    );
    if (missingRequiredFilters.length > 0) {
      const params = missingRequiredFilters.map((f) => `@${String(f.paramName).trim()}`).join(', ');
      setError(`Selecciona un valor de previsualización para ${params} en Propiedades del gráfico > Filtros dinámicos, o habilita "Ejecutar aunque no tenga selección".`);
      return;
    }

    // Validar antes de ejecutar. Si falla, intentar con la última query del chat (editor desincronizado)
    const validationMsg = validateSQL(userQuery);
    if (validationMsg) {
      const lastAssistantMessage = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.query);
      const chatQuery = lastAssistantMessage?.query?.trim();
      const chatValidationMsg = chatQuery ? validateSQL(chatQuery) : 'sin query de chat';
      if (chatQuery && !chatValidationMsg) {
        userQuery = chatQuery;
        setQuery(chatQuery);
      } else {
        setError(validationMsg);
        return;
      }
    }

    // Detectar si la query fue modificada manualmente comparándola con la última query del chat
    const lastAssistantMessage = [...chatMessages].reverse().find(msg => msg.role === 'assistant' && msg.query);
    const lastQueryInChat = lastAssistantMessage?.query?.trim() || '';
    const currentQueryTrimmed = userQuery.trim();
    
    // Preparar el chat actualizado (puede incluir mensaje de modificación manual)
    let updatedChatMessages = [...chatMessages];
    
    // Si la query actual es diferente de la última en el chat, agregar un mensaje indicando la modificación
    if (lastQueryInChat && currentQueryTrimmed !== lastQueryInChat) {
      const modificationMessage = {
        role: 'user',
        content: `He modificado manualmente la query SQL. La query actual es:\n\n${currentQueryTrimmed}`,
        timestamp: new Date().toISOString()
      };
      updatedChatMessages = [...updatedChatMessages, modificationMessage];
      setChatMessages(updatedChatMessages);
    }

    setLoading(true);
    setError(null);

    try {
      // Asegurarse de usar la BD actual del selector
      // No enviamos USE en la query, el servidor maneja la BD con el parámetro database
      if (!currentConnectionId && dataConnections.length === 0) {
        setError('No hay conexión de datos configurada. Contacta al administrador.');
        setLoading(false);
        return;
      }
      if (!currentDatabase) {
        setError('Por favor selecciona una base de datos');
        setLoading(false);
        return;
      }

      // Si la query muestra el literal anterior (tras cambiar el filtro), reemplazar por @Param y enviar el valor actual
      let userQueryToRun = userQuery;
      let parametersToSend = {};
      if (lastReplacedLiteral?.snippet && userQuery.includes(lastReplacedLiteral.snippet)) {
        userQueryToRun = replaceLiteralWithParam(userQuery, lastReplacedLiteral.paramName, lastReplacedLiteral.literalValue, lastReplacedLiteral.snippet);
        parametersToSend[lastReplacedLiteral.paramName] = lastReplacedLiteral.literalValue;
      }
      if (Object.keys(previewFilterValues || {}).length > 0) {
        parametersToSend = { ...parametersToSend, ...previewFilterValues };
      }
      activeDynamicFiltersForPreview.forEach((f) => {
        const param = String(f.paramName || '').trim();
        const selectedVal = f.selectedValue;
        if (param && selectedVal != null && String(selectedVal).trim() !== '') {
          parametersToSend[param] = selectedVal;
        }
      });

      // Construir query con USE statement
      const fullQuery = `USE [${currentDatabase}];\n${userQueryToRun}`;
      
      console.log('▶️ Ejecutando query');
      console.log('   BD seleccionada (currentDatabase):', currentDatabase);
      console.log('   Query completa:', fullQuery.substring(0, 80) + '...');
      
      const response = await executeQueryMutation.mutateAsync({
        query: fullQuery,
        database: currentDatabase,
        connectionId: currentConnectionId || selectedConnectionId || undefined,
        timezone: timezone,
        ...(Object.keys(parametersToSend).length > 0 ? { parameters: parametersToSend } : {}),
      });
      
      if (response.data.success) {
        setSaveHintParamQuery(false);
        // Si el checkbox de comparar está activo, guardar el resultado anterior
        if (compareResults && queryResult) {
          setPreviousQueryResult(queryResult);
        }
        
        setQueryResult(response.data);
        onQueryExecute(response.data, userQuery, currentDatabase);
        
        // Guardar automáticamente la query después de ejecutarla exitosamente
        // Pasar el chat actualizado (que puede incluir la modificación manual)
        if (selectedQueryId) {
          // Si hay query seleccionada, actualizar y guardar
          saveExecutionSnapshot(userQuery, response.data, updatedChatMessages);
        } else {
          // Si no hay query seleccionada, crear y guardar una nueva
          await handleSaveQueryAuto(userQuery, response.data, currentDatabase, updatedChatMessages);
        }
      } else {
        setError(response.data.error || 'Error al ejecutar la query');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error desconocido';
      setError(errorMessage);
      // Si falla por parámetro no declarado (@Param), es esperado para filtro dinámico: se puede guardar igual
      const isParamError = /scalar variable|Must declare|parámetro|@\w+/.test(String(errorMessage));
      setSaveHintParamQuery(!!isParamError);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      executeQuery();
    }
  };

  const deleteQueryMutation = useMutation({
    mutationFn: (queryId) => axios.delete(`/api/queries/${queryId}`, { headers: getAuthHeaders() }),
  });

  const handleDeleteQuery = async (queryId, event) => {
    event.stopPropagation();
    if (window.confirm('¿Eliminar esta query?')) {
      // Eliminar de BD si está autenticado
      if (isAuthenticated && authToken && (managerId || adminId)) {
        try {
          await deleteQueryMutation.mutateAsync(queryId);
        } catch (error) {
          console.error('Error al eliminar query de BD:', error);
          // Continuar con actualización local aunque falle el backend
        }
      }
      
      const updatedQueries = savedQueries.filter(q => q.id !== queryId);
      onQueriesChange(updatedQueries);
      setMenuAnchor(null);
    }
  };

  const handleRenameQuery = (queryId) => {
    const queryToRename = savedQueries.find(q => q.id === queryId);
    if (queryToRename) {
      setEditingQueryId(queryId);
      setEditName(queryToRename.name || '');
      setMenuAnchor(null);
    }
  };

  const handleSaveRename = async () => {
    if (!editName.trim()) return;
    
    // La lista es ligera; cargar query completa para no perder query/queryData/chatHistory al guardar
    const fullQuery = await fetchFullQuery(editingQueryId);
    const base = fullQuery || savedQueries.find(q => q.id === editingQueryId);
    if (!base) return;
    
    const updatedQuery = { ...base, name: editName.trim() };
    
    if (isAuthenticated && authToken && (managerId || adminId) && onSaveQuery) {
      try {
        const filterConfig = (fullQuery && (fullQuery.suggestedFilters?.length || Object.keys(fullQuery.suggestedFilterDefaults || {}).length))
          ? { filters: fullQuery.suggestedFilters || [], defaultValues: fullQuery.suggestedFilterDefaults || {} }
          : null;
        const dynamicFilterConfig = (queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName) ? queryDynamicFilterConfig : ((fullQuery?.dynamicFilterConfig && typeof fullQuery.dynamicFilterConfig === 'object') ? fullQuery.dynamicFilterConfig : null);
        await onSaveQuery(
          updatedQuery.query,
          updatedQuery.queryData || null,
          updatedQuery.database,
          updatedQuery.id,
          updatedQuery.name,
          updatedQuery.chatHistory || [],
          filterConfig,
          dynamicFilterConfig,
          updatedQuery.connectionId || currentConnectionId || selectedConnectionId
        );
      } catch (error) {
        console.error('Error al guardar renombrado en BD:', error);
      }
    }
    
    // Actualizar solo el nombre en la lista ligera (el padre ya puede haber actualizado con saveQuery)
    onQueriesChange(savedQueries.map(q =>
      q.id === editingQueryId ? { ...q, name: editName.trim() } : q
    ));
    setEditingQueryId(null);
    setEditName('');
  };

  const handleSelectQuery = async (queryItem) => {
    // La lista es ligera; cargar query completa desde API
    setLoadingFullQuery(true);
    try {
      const fullQuery = await fetchFullQuery(queryItem.id);
      if (fullQuery) {
        applyFullQueryToState(fullQuery);
      } else {
        // Fallback: usar solo datos ligeros (nombre, BD)
        if (queryItem.database) {
          setCurrentDatabase(queryItem.database);
          if (onDatabaseChange) onDatabaseChange(queryItem.database);
        }
        if (queryItem.connectionId) {
          setCurrentConnectionId(queryItem.connectionId);
          loadDatabasesForConnection(queryItem.connectionId);
        }
        setSelectedQueryId(queryItem.id);
        setChatMessages([]);
        setQuery(queryItem.query || '');
        setQueryResult(null);
        setSqlTabValue(0);
        setSuggestedFiltersFromAi([]);
        setPreviewFilterValues({});
      }
    } finally {
      setLoadingFullQuery(false);
    }
  };

  // Función eliminada: handleSelectVersion - ya no necesaria, las versiones son queries en el chat
  // Función eliminada: handleSelectHistoryEntry - el historial está en el chat

  const saveExecutionSnapshot = async (executedQuery, queryData, chatMessagesToUse = null) => {
    // Usar el chat pasado como parámetro o el estado actual
    // Asegurarse de que siempre sea un array
    const currentChat = Array.isArray(chatMessagesToUse) ? chatMessagesToUse : (Array.isArray(chatMessages) ? chatMessages : []);
    
    // Agregar la ejecución al chat como mensaje del asistente
    const executionMessage = {
      role: 'assistant',
      content: `Query ejecutada exitosamente. ${queryData?.data?.length || 0} filas obtenidas.`,
      query: executedQuery,
      timestamp: new Date().toISOString(),
      executed: true
    };
    
    // Agregar al chat si no existe ya (evitar duplicados)
    let updatedChatMessages;
    const lastMessage = currentChat.length > 0 ? currentChat[currentChat.length - 1] : null;
    if (lastMessage && lastMessage.query === executedQuery && lastMessage.executed) {
      updatedChatMessages = currentChat; // Ya existe, no duplicar
    } else {
      updatedChatMessages = [...currentChat, executionMessage];
    }
    
    // Actualizar el estado del chat
    setChatMessages(updatedChatMessages);
    
    // Si hay query seleccionada, actualizar con el chat completo y la query actual
    if (selectedQueryId) {
      const queryToUpdate = savedQueries.find(q => q.id === selectedQueryId);
      if (queryToUpdate) {
        const updatedQuery = { 
          ...queryToUpdate, 
          query: executedQuery, 
          queryData: queryData,
          chatHistory: updatedChatMessages // Usar el chat actualizado (incluye modificaciones manuales)
        };
        
        // Guardar en BD si está autenticado; App actualiza la lista ligera (incluir filtros sugeridos actuales)
        if (isAuthenticated && authToken && (managerId || adminId) && onSaveQuery) {
          try {
            const filterConfig = (suggestedFiltersFromAi?.length || Object.keys(previewFilterValues || {}).length)
              ? { filters: suggestedFiltersFromAi || [], defaultValues: previewFilterValues || {} }
              : null;
            const dynamicFilterConfig = (queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName) ? queryDynamicFilterConfig : null;
            await onSaveQuery(
              updatedQuery.query,
              updatedQuery.queryData,
              updatedQuery.database,
              updatedQuery.id,
              updatedQuery.name,
              updatedQuery.chatHistory,
              filterConfig,
              dynamicFilterConfig,
              updatedQuery.connectionId || currentConnectionId || selectedConnectionId
            );
          } catch (error) {
            console.error('Error al guardar snapshot en BD:', error);
          }
        }
        // No llamar onQueriesChange: no volcar datos pesados en la lista
      }
    }
  };

  // Función para guardar automáticamente después de ejecutar
  const handleSaveQueryAuto = async (userQuery, queryDataResult, database, chatMessagesToUse = null) => {
    if (!isAuthenticated || !authToken || (!managerId && !adminId) || !onSaveQuery) {
      return null;
    }

    // Usar el chat pasado como parámetro o el estado actual
    const currentChat = chatMessagesToUse || chatMessages;
    
    // Agregar mensaje de ejecución al chat antes de guardar
    const executionMessage = {
      role: 'assistant',
      content: `Query ejecutada exitosamente. ${queryDataResult?.data?.length || 0} filas obtenidas.`,
      query: userQuery,
      timestamp: new Date().toISOString(),
      executed: true
    };
    
    const updatedChat = [...currentChat, executionMessage];
    setChatMessages(updatedChat);

    const queryEntry = {
      id: null,
      query: userQuery,
      queryData: queryDataResult || null,
      database: database || currentDatabase,
      createdAt: new Date().toISOString(),
      name: `Query ${new Date().toLocaleTimeString()}`,
      chatHistory: updatedChat // Guardar el chat completo (incluye modificaciones manuales)
    };

    try {
      const filterConfig = (suggestedFiltersFromAi?.length || Object.keys(previewFilterValues || {}).length)
        ? { filters: suggestedFiltersFromAi || [], defaultValues: previewFilterValues || {} }
        : null;
      const dynamicFilterConfig = (queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName) ? queryDynamicFilterConfig : null;
      const savedId = await onSaveQuery(
        queryEntry.query,
        queryEntry.queryData,
        queryEntry.database,
        queryEntry.id,
        queryEntry.name,
        queryEntry.chatHistory,
        filterConfig,
        dynamicFilterConfig,
        currentConnectionId || selectedConnectionId
      );
      setSelectedQueryId(savedId);
      // No llamar onQueriesChange: App ya actualizó savedQueries con un ítem ligero
      return savedId;
    } catch (error) {
      console.error('Error al guardar query automáticamente:', error);
      setError('La query se ejecutó pero no se pudo guardar. Intenta guardar manualmente con el botón Guardar.');
      return null;
    }
  };

  const handleSaveQuery = async () => {
    let userQuery = query.trim();
    if (!userQuery.trim()) {
      setError('No hay query para guardar');
      return;
    }

    // Validar antes de guardar. Si falla, intentar con la última query del chat (puede estar desincronizado el editor)
    const validationMsg = validateSQL(userQuery);
    if (validationMsg) {
      const lastAssistantMessage = [...chatMessages].reverse().find(m => m.role === 'assistant' && m.query);
      const chatQuery = lastAssistantMessage?.query?.trim();
      const chatValidationMsg = chatQuery ? validateSQL(chatQuery) : 'sin query de chat';
      if (chatQuery && !chatValidationMsg) {
        userQuery = chatQuery;
        setQuery(chatQuery); // Sincronizar editor con la query válida del chat
      } else {
        setError(validationMsg);
        return;
      }
    }

    const existingQuery = savedQueries.find(q => q.id === selectedQueryId);

    // Si hay resultado, guardar con él; si no, guardar solo la query
    const queryEntry = {
      id: selectedQueryId || null,
      query: userQuery, // Guardar solo la query del usuario, sin USE
      queryData: queryResult || null,
      database: currentDatabase,
      createdAt: new Date().toISOString(),
      name: existingQuery?.name || `Query ${new Date().toLocaleTimeString()}`,
      chatHistory: chatMessages // Guardar el chat completo como fuente de verdad
    };

    // Usar onSaveQuery si está disponible (desde App.js); el padre actualiza la lista ligera (incluir filtros sugeridos)
    if (onSaveQuery) {
      try {
        const filterConfig = (suggestedFiltersFromAi?.length || Object.keys(previewFilterValues || {}).length)
          ? { filters: suggestedFiltersFromAi || [], defaultValues: previewFilterValues || {} }
          : null;
        const dynamicFilterConfig = (queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName) ? queryDynamicFilterConfig : null;
        const savedId = await onSaveQuery(
          queryEntry.query,
          queryEntry.queryData,
          queryEntry.database,
          queryEntry.id,
          queryEntry.name,
          queryEntry.chatHistory,
          filterConfig,
          dynamicFilterConfig,
          currentConnectionId || selectedConnectionId
        );
        setSelectedQueryId(savedId);
        // No llamar onQueriesChange: App ya actualizó savedQueries con un ítem ligero
      } catch (error) {
        console.error('Error al guardar query:', error);
        setError('Error al guardar query');
        return;
      }
    } else {
      // Fallback al método anterior si onSaveQuery no está disponible
      const existingIndex = savedQueries.findIndex(q => q.id === queryEntry.id);
      let updatedQueries;
      if (existingIndex >= 0) {
        updatedQueries = [...savedQueries];
        updatedQueries[existingIndex] = queryEntry;
      } else {
        updatedQueries = [queryEntry, ...savedQueries].slice(0, 20);
        setSelectedQueryId(queryEntry.id);
      }
      onQueriesChange(updatedQueries);
    }
    
    // Mostrar feedback de guardado
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const loadSchemaExampleMutation = useMutation({
    mutationFn: () =>
      axios.get('/api/schema', {
        params: {
          database: currentDatabase,
          connectionId: currentConnectionId || selectedConnectionId || undefined,
        },
        headers: getAuthHeaders(),
      }),
  });

  const loadSchemaExample = async () => {
    try {
      const response = await loadSchemaExampleMutation.mutateAsync();
      if (response.data.success) {
        const schemaQuery = `SELECT s.name AS [Schema], t.name AS [Table], c.column_id AS [ColumnOrder], c.name AS [Column], ty.name AS [DataType] FROM sys.tables t JOIN sys.schemas s ON s.schema_id = t.schema_id JOIN sys.columns c ON c.object_id = t.object_id JOIN sys.types ty ON ty.user_type_id = c.user_type_id WHERE s.name = 'dbo' ORDER BY s.name, t.name, c.column_id`;
        setQuery(schemaQuery);
        setExamplesMenuAnchor(null);
      }
    } catch (error) {
      console.error('Error al cargar esquema:', error);
      showError('Error al cargar el esquema: ' + (error.response?.data?.error || error.message));
    }
  };

  const getAvailableColumns = () => {
    if (!queryResult?.columns) return [];
    if (Array.isArray(queryResult.columns)) {
      return queryResult.columns;
    }
    if (queryResult.data && queryResult.data.length > 0) {
      return Object.keys(queryResult.data[0] || {});
    }
    return [];
  };

  const normalizeChartDynamicFilters = (advCfg) => {
    if (Array.isArray(advCfg?.dynamicFilters)) return advCfg.dynamicFilters.filter((f) => f && typeof f === 'object');
    if (advCfg?.dynamicFilter && typeof advCfg.dynamicFilter === 'object') return [advCfg.dynamicFilter];
    return [];
  };

  const withSafeInitialDynamicFilterValues = (filters) => {
    const today = new Date().toISOString().slice(0, 10);
    return (Array.isArray(filters) ? filters : []).map((f) => {
      if (!f || typeof f !== 'object') return f;
      if (f.filterType === 'date' && (f.selectedValue == null || String(f.selectedValue).trim() === '')) {
        return { ...f, selectedValue: today };
      }
      return f;
    });
  };

  const mergedDynamicFiltersForChart = useMemo(() => {
    const base = normalizeChartDynamicFilters(previewAdvancedConfig);
    const out = [...base];
    if (queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName) {
      const p = String(queryDynamicFilterConfig.paramName).trim();
      if (!out.some((f) => String(f?.paramName || '').trim() === p)) {
        out.push({ ...queryDynamicFilterConfig, enabled: true });
      }
    }
    return withSafeInitialDynamicFilterValues(out);
  }, [previewAdvancedConfig, queryDynamicFilterConfig]);

  const activeDynamicFiltersForPreview = useMemo(
    () => mergedDynamicFiltersForChart.filter((f) => f?.enabled && String(f?.paramName || '').trim() !== ''),
    [mergedDynamicFiltersForChart]
  );
  const missingRequiredPreviewFilters = useMemo(
    () =>
      activeDynamicFiltersForPreview.filter(
        (f) => f.allowExecuteWithoutSelection !== true && (f.selectedValue == null || String(f.selectedValue).trim() === '')
      ),
    [activeDynamicFiltersForPreview]
  );

  // Valor estable para ChartEditDialog (incluye filtros dinámicos múltiples de previsualización).
  const chartValueForPreview = useMemo(() => ({
    type: previewType,
    xColumn: previewXColumn,
    yColumn: previewYColumn,
    groupByColumn: previewGroupByColumn,
    labelColumn: previewLabelColumn,
    stacked: previewStacked,
    advancedConfig: {
      ...(previewAdvancedConfig ?? {}),
      dynamicFilters: mergedDynamicFiltersForChart,
      dynamicFilter: mergedDynamicFiltersForChart[0] || { enabled: false, paramName: '' },
    },
    heatmapGridConfig: previewHeatmapGridConfig,
    mapConfig: previewMapConfig,
  }), [previewType, previewXColumn, previewYColumn, previewGroupByColumn, previewLabelColumn, previewStacked, previewAdvancedConfig, previewHeatmapGridConfig, previewMapConfig, mergedDynamicFiltersForChart]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Barra superior */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Editor SQL
        </Typography>
        <Button
          startIcon={<MenuBookIcon />}
          onClick={(e) => setExamplesMenuAnchor(e.currentTarget)}
          variant="outlined"
          size="small"
        >
          Ejemplos
        </Button>
        <Menu
          anchorEl={examplesMenuAnchor}
          open={Boolean(examplesMenuAnchor)}
          onClose={() => setExamplesMenuAnchor(null)}
        >
          <MenuItem onClick={loadSchemaExample}>
            Esquema de Base de Datos
          </MenuItem>
        </Menu>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveQuery}
          disabled={!query.trim() || !!validationError}
          size="small"
          color={saveSuccess ? 'success' : 'primary'}
        >
          {saveSuccess ? 'Guardado ✓' : 'Guardar'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => { setError(null); setSaveHintParamQuery(false); }}>
          {error}
        </Alert>
      )}
      {saveHintParamQuery && (
        <Alert severity="info" sx={{ m: 2 }} onClose={() => setSaveHintParamQuery(false)}>
          Esta query usa parámetros para el filtro dinámico. No hace falta ejecutarla aquí: usa el botón <strong>Guardar</strong> para guardar la query. En el dashboard el gráfico usará el parámetro al elegir un valor en el desplegable. Si el filtro muestra solo un valor, en Datos del gráfico → Filtro dinámico vuelve a pulsar &quot;Sugerir desde la query&quot; y elige la misma opción (así cargará todos los valores).
        </Alert>
      )}

      {connectionStatus === 'error' && (
        <Alert severity="warning" sx={{ m: 2 }}>
          No se pudo conectar a la base de datos.
          {connectionErrorDetail ? ` ${connectionErrorDetail}` : ' Verifica la configuración.'}
        </Alert>
      )}

      {/* Layout de 3 paneles */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <PanelGroup direction="horizontal" style={{ height: '100%' }}>
          {/* Panel izquierdo - Lista de queries */}
          <Panel defaultSize={17} minSize={15} maxSize={30} style={{ overflow: 'auto' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {loadingFullQuery && (
                <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Cargando query…</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <QueryFolderSidebar
                  savedQueries={savedQueries}
                  selectedQueryId={selectedQueryId}
                  isNewQueryDraft={isNewQueryDraft}
                  onNewQuery={handleNewQuery}
                  onSelectQuery={(queryId) => {
                    const q = savedQueries.find((item) => item.id === queryId);
                    if (q) handleSelectQuery(q);
                  }}
                  onQueriesChange={onQueriesChange}
                  getAuthHeaders={getAuthHeaders}
                  open
                />
              </Box>
            </Box>
          </Panel>

          <PanelResizeHandle style={{ width: '4px', backgroundColor: 'rgba(0, 0, 0, 0.1)', cursor: 'col-resize' }} />

          {/* Panel medio - Editor SQL */}
          <Panel defaultSize={33} minSize={25}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
              <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: '#1e1e1e' }}>
                {queryDataSourceBar}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={sqlTabValue} 
                    onChange={(e, newValue) => setSqlTabValue(newValue)}
                    sx={{
                      minHeight: '36px',
                      '& .MuiTab-root': {
                        minHeight: '36px',
                        padding: '8px 16px',
                        fontSize: '0.75rem',
                        color: '#858585',
                        '&.Mui-selected': {
                          color: '#90caf9',
                        },
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#90caf9',
                      },
                    }}
                  >
                    <Tab label="Query SQL" />
                    <Tab label="Filtros" />
                  </Tabs>
                </Box>
                
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Sección de Query SQL */}
                  {sqlTabValue === 0 && (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ p: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={compareResults}
                              onChange={(e) => setCompareResults(e.target.checked)}
                              size="small"
                              sx={{ color: '#90caf9' }}
                            />
                          }
                          label={
                            <Tooltip title="Al ejecutar una nueva query, muestra el resultado anterior lado a lado para comparar cambios">
                              <Typography variant="caption" sx={{ color: '#858585', fontSize: '0.75rem', cursor: 'help' }}>
                                Comparar resultados
                              </Typography>
                            </Tooltip>
                          }
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          variant="contained"
                          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                          onClick={() => {
                            if (!loading) executeQuery();
                          }}
                          // loading no deshabilita el botón (se vería gris); el propio onClick
                          // ignora el clic mientras carga, así se mantiene el morado normal.
                          disabled={
                            !!validationError ||
                            !query.trim() ||
                            missingRequiredPreviewFilters.length > 0
                          }
                          size="small"
                          sx={{ minWidth: 140 }}
                        >
                          {loading ? 'Ejecutando...' : 'Ejecutar Query'}
                        </Button>
                      </Box>
                      <TextField
                        inputRef={textareaRef}
                        multiline
                        fullWidth
                        value={query}
                        onChange={(e) => {
                          setQuery(e.target.value);
                          validateSQL(e.target.value);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Escribe tu query SQL aquí..."
                        variant="outlined"
                        error={!!validationError}
                        helperText={validationError || ''}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                        FormHelperTextProps={{
                          sx: {
                            position: 'absolute',
                            bottom: -20,
                            left: 0,
                            m: 0,
                            fontSize: '0.75rem',
                          }
                        }}
                        sx={{
                          flex: 1,
                          height: '100%',
                          '& .MuiInputBase-root': {
                            height: '100%',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            color: '#d4d4d4',
                            backgroundColor: '#1e1e1e',
                            '& fieldset': {
                              borderColor: validationError 
                                ? 'rgba(211, 47, 47, 0.5)' 
                                : 'rgba(255, 255, 255, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: validationError 
                                ? 'rgba(211, 47, 47, 0.7)' 
                                : 'rgba(255, 255, 255, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: validationError 
                                ? '#ef5350' 
                                : '#90caf9',
                            },
                          },
                          '& .MuiInputBase-input': {
                            height: '100% !important',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            color: '#d4d4d4',
                            padding: 2,
                            overflow: 'auto !important',
                            '&::placeholder': {
                              color: '#858585',
                              opacity: 1,
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: validationError ? '#ef5350' : 'inherit',
                          },
                        }}
                        InputProps={{
                          style: {
                            height: '100%',
                            alignItems: 'flex-start',
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Pestaña Filtros: configuración del filtro dinámico a nivel de query */}
                  {sqlTabValue === 1 && (
                    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#d4d4d4' }}>
                        Filtro dinámico
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#858585', mb: 2 }}>
                        Configura un parámetro (@NombreParam) en la query. Al ejecutar o en el dashboard, se usará el valor de muestra o el elegido en el desplegable.
                      </Typography>
                      {mergedDynamicFiltersForChart.length > 1 && (
                        <Paper variant="outlined" sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)' }}>
                          <Typography variant="caption" sx={{ color: '#90caf9', display: 'block', mb: 0.75 }}>
                            Filtros dinámicos detectados para este gráfico ({mergedDynamicFiltersForChart.length})
                          </Typography>
                          <List dense disablePadding>
                            {mergedDynamicFiltersForChart.map((f, idx) => {
                              const p = String(f?.paramName || '').trim();
                              return (
                                <ListItemButton
                                  key={`multi-filter-${idx}-${p || 'sin-param'}`}
                                  onClick={() =>
                                    setQueryDynamicFilterConfig({
                                      enabled: f?.enabled !== false,
                                      paramName: p,
                                      filterType: f?.filterType || 'select',
                                      optionSource: f?.optionSource || 'query',
                                      optionsQuery: f?.optionsQuery || '',
                                      labelColumn: f?.labelColumn || '',
                                      valueColumn: f?.valueColumn || '',
                                      staticOptions: Array.isArray(f?.staticOptions) ? f.staticOptions : [],
                                      selectedValue: f?.selectedValue ?? '',
                                      allowExecuteWithoutSelection: f?.allowExecuteWithoutSelection === true,
                                    })
                                  }
                                  sx={{ color: '#d4d4d4' }}
                                >
                                  <ListItemText
                                    primary={`@${p || `Filtro${idx + 1}`}`}
                                    secondary={`${f?.filterType === 'date' ? 'Fecha' : (f?.optionSource === 'static' ? 'Opciones estáticas' : 'Opciones por query')}${f?.enabled === false ? ' · desactivado' : ''}`}
                                    primaryTypographyProps={{ sx: { color: '#d4d4d4' } }}
                                    secondaryTypographyProps={{ sx: { color: '#858585' } }}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                          <Typography variant="caption" sx={{ color: '#858585', mt: 0.75, display: 'block' }}>
                            Selecciona uno para cargarlo en el editor de abajo.
                          </Typography>
                        </Paper>
                      )}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={queryDynamicFilterConfig.enabled === true}
                                onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                  ...prev,
                                  enabled: e.target.checked,
                                }))}
                                sx={{ color: '#90caf9' }}
                              />
                            }
                            label={<Typography sx={{ color: '#d4d4d4' }}>Activar filtro dinámico</Typography>}
                          />
                        </Grid>
                        {queryDynamicFilterConfig.enabled && (
                          <>
                            {query.trim() && (
                              <Grid item xs={12}>
                                <Button
                                  size="small"
                                  startIcon={<AutoAwesomeIcon />}
                                  onClick={() => {
                                    const candidates = detectFilterCandidates(query, dynamicFilterOptionSources);
                                    setFilterCandidates(candidates);
                                  }}
                                  sx={{ color: '#90caf9', borderColor: 'rgba(144, 202, 249, 0.5)' }}
                                  variant="outlined"
                                >
                                  Sugerir desde la query
                                </Button>
                                {filterCandidates.length > 0 && (
                                  <Paper variant="outlined" sx={{ mt: 1, p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'rgba(0,0,0,0.2)' }}>
                                    <Typography variant="caption" sx={{ color: '#858585', px: 1, display: 'block' }}>
                                      Clic en uno para usarlo como filtro (se reemplazarán ocurrencias por @param en la query):
                                    </Typography>
                                    <List dense disablePadding>
                                      {filterCandidates.map((cand, idx) => (
                                        <ListItemButton
                                          key={idx}
                                          onClick={() => {
                                            if (cand.candidateType && String(cand.candidateType).startsWith('date-')) {
                                              const paramConfigs = Array.isArray(cand.paramConfigs) ? cand.paramConfigs : [];
                                              if (paramConfigs.length > 0) {
                                                mergePreviewDynamicFilters(paramConfigs);
                                                const first = paramConfigs[0];
                                                if (first?.paramName) {
                                                  setQueryDynamicFilterConfig(prev => ({
                                                    ...prev,
                                                    enabled: true,
                                                    paramName: String(first.paramName).trim(),
                                                    filterType: first.filterType || 'select',
                                                    optionSource: first.optionSource || 'query',
                                                    optionsQuery: first.optionsQuery || '',
                                                    valueColumn: first.valueColumn || String(first.paramName).trim(),
                                                    labelColumn: first.labelColumn || String(first.paramName).trim(),
                                                    staticOptions: Array.isArray(first.staticOptions) ? first.staticOptions : [],
                                                    allowExecuteWithoutSelection: first.allowExecuteWithoutSelection === true,
                                                  }));
                                                }
                                              }
                                              if (cand.candidateType === 'date-range' && cand.dateColumnExpr) {
                                                const newQuery = applyDateRangeSuggestionToQuery(query, cand.dateColumnExpr);
                                                if (newQuery !== query) setQuery(newQuery);
                                              } else if (cand.candidateType === 'date-part-year' && cand.dateColumnExpr) {
                                                const newQuery = applyDatePartSuggestionToQuery(query, cand.dateColumnExpr, 'YEAR');
                                                if (newQuery !== query) setQuery(newQuery);
                                              } else if (cand.candidateType === 'date-part-month' && cand.dateColumnExpr) {
                                                const newQuery = applyDatePartSuggestionToQuery(query, cand.dateColumnExpr, 'MONTH');
                                                if (newQuery !== query) setQuery(newQuery);
                                              }
                                              setFilterCandidates([]);
                                              return;
                                            }
                                            const paramName = cand.canonicalParamName ?? cand.columnName;
                                            const useQueryAll = cand.suggestedOptionsQuery;
                                            const literalValue = cand.values && cand.values[0];
                                            if (useQueryAll) {
                                              setQueryDynamicFilterConfig(prev => ({
                                                ...prev,
                                                paramName,
                                                filterType: 'select',
                                                optionSource: 'query',
                                                optionsQuery: cand.suggestedOptionsQuery || '',
                                                valueColumn: cand.suggestedValueColumn ?? paramName,
                                                labelColumn: cand.suggestedLabelColumn ?? paramName,
                                                staticOptions: [],
                                              }));
                                            } else {
                                              const staticOpts = (cand.values || []).map(v => ({ label: String(v), value: v }));
                                              setQueryDynamicFilterConfig(prev => ({
                                                ...prev,
                                                paramName,
                                                filterType: 'select',
                                                optionSource: staticOpts.length > 0 ? 'static' : 'query',
                                                staticOptions: staticOpts,
                                                ...(staticOpts.length === 0 ? {} : { optionsQuery: '', labelColumn: '', valueColumn: '' }),
                                              }));
                                            }
                                            setFilterCandidates([]);
                                            handleReplaceQueryLiteral(paramName, literalValue, cand.snippet);
                                          }}
                                          sx={{ color: '#d4d4d4' }}
                                        >
                                          <ListItemText
                                            primary={cand.snippet}
                                            secondary={
                                              cand.candidateType
                                                ? (cand.candidateType === 'date-range'
                                                  ? 'Crea @FechaInicio/@FechaFin y adapta el WHERE'
                                                  : (cand.candidateType === 'date-part-year'
                                                    ? 'Crea @Anio y sugiere lista de años'
                                                    : 'Crea @Mes (1..12)'))
                                                : (cand.suggestedOptionsQuery ? 'Cargar todos los valores desde la tabla' : (cand.values?.length ? `${cand.values.length} valor(es) en query` : null))
                                            }
                                            primaryTypographyProps={{ sx: { color: '#90caf9' } }}
                                            secondaryTypographyProps={{ sx: { color: '#858585' } }}
                                          />
                                        </ListItemButton>
                                      ))}
                                    </List>
                                  </Paper>
                                )}
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Nombre del parámetro en la query (@param)"
                                value={queryDynamicFilterConfig.paramName || ''}
                                onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                  ...prev,
                                  paramName: e.target.value,
                                }))}
                                fullWidth
                                size="small"
                                placeholder="ej. ProductoId"
                                sx={{
                                  '& .MuiInputBase-input': { color: '#d4d4d4' },
                                  '& .MuiInputLabel-root': { color: '#858585' },
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' },
                                }}
                              />
                            </Grid>
                            {queryDynamicFilterConfig.optionSource === 'static' && (queryDynamicFilterConfig.staticOptions?.length ?? 0) > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: '#858585' }}>
                                  Opciones desde la query: {queryDynamicFilterConfig.staticOptions.length} valor(es). Reemplaza en tu SQL el literal por @{queryDynamicFilterConfig.paramName || 'param'}.
                                </Typography>
                              </Grid>
                            )}
                            {queryDynamicFilterConfig.optionSource !== 'static' && (
                              <>
                                <Grid item xs={12}>
                                  <TextField
                                    label="Query de opciones (SELECT para llenar el desplegable)"
                                    value={queryDynamicFilterConfig.optionsQuery || ''}
                                    onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                      ...prev,
                                      optionsQuery: e.target.value,
                                    }))}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    placeholder="SELECT Id, Nombre FROM Productos"
                                    sx={{
                                      '& .MuiInputBase-input': { color: '#d4d4d4' },
                                      '& .MuiInputLabel-root': { color: '#858585' },
                                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Columna para el valor (se envía como parámetro)"
                                    value={queryDynamicFilterConfig.valueColumn || ''}
                                    onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                      ...prev,
                                      valueColumn: e.target.value,
                                    }))}
                                    fullWidth
                                    size="small"
                                    placeholder="ej. Id"
                                    sx={{
                                      '& .MuiInputBase-input': { color: '#d4d4d4' },
                                      '& .MuiInputLabel-root': { color: '#858585' },
                                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Columna para mostrar en el desplegable"
                                    value={queryDynamicFilterConfig.labelColumn || ''}
                                    onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                      ...prev,
                                      labelColumn: e.target.value,
                                    }))}
                                    fullWidth
                                    size="small"
                                    placeholder="ej. Nombre"
                                    sx={{
                                      '& .MuiInputBase-input': { color: '#d4d4d4' },
                                      '& .MuiInputLabel-root': { color: '#858585' },
                                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' },
                                    }}
                                  />
                                </Grid>
                              </>
                            )}
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={queryDynamicFilterConfig.allowExecuteWithoutSelection === true}
                                    onChange={(e) => setQueryDynamicFilterConfig(prev => ({
                                      ...prev,
                                      allowExecuteWithoutSelection: e.target.checked,
                                    }))}
                                    sx={{ color: '#90caf9' }}
                                  />
                                }
                                label={
                                  <Typography sx={{ color: '#d4d4d4', fontSize: '0.875rem' }}>
                                    Ejecutar aunque no tenga selección de filtro
                                  </Typography>
                                }
                              />
                              <Typography variant="caption" display="block" sx={{ color: '#858585', mt: 0.5 }}>
                                Si está desactivado (recomendado), no se ejecutará la query hasta que elijas un valor en el selector de la previsualización, para evitar traer demasiados datos.
                              </Typography>
                            </Grid>
                            {(queryDynamicFilterConfig.optionSource === 'static' && (queryDynamicFilterConfig.staticOptions?.length ?? 0) === 0) && (
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: '#858585', mb: 1 }}>
                                  Sin opciones estáticas. Añade una query de opciones arriba o vuelve a &quot;Sugerir desde la query&quot; con un IN (...).
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => setQueryDynamicFilterConfig(prev => ({ ...prev, optionSource: 'query' }))}
                                  sx={{ color: '#90caf9' }}
                                >
                                  Usar query de opciones
                                </Button>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Panel>

          <PanelResizeHandle style={{ width: '4px', backgroundColor: 'rgba(0, 0, 0, 0.1)', cursor: 'col-resize' }} />

          {/* Panel derecho - Preview de resultados */}
          <Panel defaultSize={50} minSize={30}>
            {queryResult || query.trim() ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {initialChart && (
                  <Chip
                    label="Editando gráfico existente"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                  {compareResults && previousQueryResult ? 'Comparación de Resultados' : (previewAdvancedConfig?.title || 'Resultados')}
                  {queryResult && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({queryResult.data?.length || 0} registros)
                    </Typography>
                  )}
                  {compareResults && previousQueryResult && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      | Anterior: ({previousQueryResult.data?.length || 0} registros)
                    </Typography>
                  )}
                </Typography>
                {queryResult && queryResult.data && queryResult.data.length > 0 && (
                  <>
                    {onAddChart && queryResult && !initialChart && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const columns = getAvailableColumns();
                          if (columns.length === 0) {
                            showWarning('No hay columnas disponibles');
                            return;
                          }
                          const baseAdvancedConfig = previewAdvancedConfig && typeof previewAdvancedConfig === 'object'
                            ? { ...previewAdvancedConfig }
                            : {};
                          const dynamicFiltersMerge = mergedDynamicFiltersForChart;
                          const dynamicFilterMerge = dynamicFiltersMerge[0];
                          const advancedConfig = {
                            ...baseAdvancedConfig,
                            ...(dynamicFilterMerge !== undefined ? { dynamicFilter: dynamicFilterMerge } : {}),
                            dynamicFilters: dynamicFiltersMerge,
                          };
                          let chartConfig;
                          if (previewType === 'table') {
                            const defaultLabel = `Tabla - ${queryResult.data?.length || 0} registros`;
                            chartConfig = {
                              type: 'table',
                              xColumn: '',
                              yColumn: '',
                              advancedConfig,
                              label: (advancedConfig.title != null && String(advancedConfig.title).trim() !== '') ? advancedConfig.title : defaultLabel,
                              queryId: selectedQueryId,
                            };
                          } else if (previewType === 'imageCarousel') {
                            const defaultLabel = `Carrusel de imágenes - ${queryResult.data?.length || 0} filas`;
                            chartConfig = {
                              type: 'imageCarousel',
                              advancedConfig,
                              label: (advancedConfig.title != null && String(advancedConfig.title).trim() !== '') ? advancedConfig.title : defaultLabel,
                              queryId: selectedQueryId,
                            };
                          } else if (previewType === 'heatmapGrid' && Number(previewHeatmapGridConfig?.gridRows) >= 1 && Number(previewHeatmapGridConfig?.gridCols) >= 1) {
                            const defaultLabel = `Heatmap ${previewHeatmapGridConfig.gridRows}x${previewHeatmapGridConfig.gridCols}`;
                            chartConfig = {
                              type: 'heatmapGrid',
                              heatmapGridConfig: { ...previewHeatmapGridConfig },
                              advancedConfig,
                              label: (advancedConfig.title != null && String(advancedConfig.title).trim() !== '') ? advancedConfig.title : defaultLabel,
                              queryId: selectedQueryId,
                            };
                          } else if (previewType === 'map' && ((previewMapConfig?.latColumn && previewMapConfig?.lngColumn) || previewMapConfig?.addressColumn)) {
                            const defaultLabel = (previewMapConfig?.latColumn && previewMapConfig?.lngColumn)
                              ? `Mapa - ${previewMapConfig.latColumn} / ${previewMapConfig.lngColumn}`
                              : `Mapa - ${previewMapConfig?.addressColumn || 'dirección'}`;
                            chartConfig = {
                              type: 'map',
                              mapConfig: { ...previewMapConfig },
                              advancedConfig,
                              label: (advancedConfig.title != null && String(advancedConfig.title).trim() !== '') ? advancedConfig.title : defaultLabel,
                              queryId: selectedQueryId,
                            };
                          } else if (previewXColumn && previewYColumn) {
                            let labelText = `${previewXColumn} vs ${previewYColumn}`;
                            if (previewGroupByColumn && previewLabelColumn) {
                              labelText += ` (agrupado por ${previewGroupByColumn}, coloreado por ${previewLabelColumn})`;
                            } else if (previewGroupByColumn) {
                              labelText += ` (agrupado por ${previewGroupByColumn})`;
                            } else if (previewLabelColumn) {
                              labelText += ` (coloreado por ${previewLabelColumn})`;
                            }
                            const defaultLabel = `Gráfico ${previewType} - ${labelText}`;
                            chartConfig = {
                              type: previewType,
                              xColumn: previewXColumn,
                              yColumn: previewYColumn,
                              groupByColumn: previewGroupByColumn || undefined,
                              labelColumn: previewLabelColumn || undefined,
                              stacked: previewType === 'bar' && (previewGroupByColumn || previewLabelColumn) ? previewStacked : undefined,
                              advancedConfig,
                              label: (advancedConfig.title != null && String(advancedConfig.title).trim() !== '') ? advancedConfig.title : defaultLabel,
                              queryId: selectedQueryId,
                            };
                          } else if (previewType === 'map') {
                            showWarning('Selecciona columnas de latitud y longitud para el mapa');
                            return;
                          } else {
                            showWarning('Por favor selecciona columnas X e Y para el gráfico');
                            return;
                          }
                          onAddChart(chartConfig, queryResult);
                        }}
                        disabled={previewType === 'table' || previewType === 'imageCarousel' ? false : previewType === 'heatmapGrid' ? !(Number(previewHeatmapGridConfig?.gridRows) >= 1 && Number(previewHeatmapGridConfig?.gridCols) >= 1) : previewType === 'map' ? !((previewMapConfig?.latColumn && previewMapConfig?.lngColumn) || previewMapConfig?.addressColumn) : (!previewXColumn || !previewYColumn)}
                        sx={{ ml: 1 }}
                      >
                        Agregar al Dashboard
                      </Button>
                    )}
                    {initialChart && onUpdateChart && onCloseAfterChartSave && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          const configToApply = {
                            type: previewType,
                            xColumn: (previewType === 'table' || previewType === 'heatmapGrid' || previewType === 'map' || previewType === 'imageCarousel') ? undefined : previewXColumn,
                            yColumn: (previewType === 'table' || previewType === 'heatmapGrid' || previewType === 'map' || previewType === 'imageCarousel') ? undefined : previewYColumn,
                            groupByColumn: previewGroupByColumn || undefined,
                            labelColumn: previewLabelColumn || undefined,
                            stacked: previewType === 'bar' && (previewGroupByColumn || previewLabelColumn) ? previewStacked : undefined,
                            advancedConfig: previewAdvancedConfig && typeof previewAdvancedConfig === 'object'
                              ? {
                                  ...previewAdvancedConfig,
                                  dynamicFilters: mergedDynamicFiltersForChart,
                                  dynamicFilter: mergedDynamicFiltersForChart[0] || (previewAdvancedConfig.dynamicFilter ? { ...previewAdvancedConfig.dynamicFilter } : undefined),
                                }
                              : {},
                            heatmapGridConfig: previewType === 'heatmapGrid' ? previewHeatmapGridConfig : undefined,
                            mapConfig: previewType === 'map' ? { ...previewMapConfig } : undefined,
                            label: (previewAdvancedConfig?.title) || initialChart.label,
                          };
                          onUpdateChart(initialChart.id, configToApply);
                          onCloseAfterChartSave(initialChart.id, {
                            queryData: queryResult,
                            queryId: selectedQueryId,
                            chartConfig: configToApply,
                            chart: { ...initialChart, ...configToApply, id: initialChart.id },
                          });
                        }}
                        disabled={previewType === 'table' || previewType === 'imageCarousel' ? false : previewType === 'heatmapGrid' ? !(Number(previewHeatmapGridConfig?.gridRows) >= 1 && Number(previewHeatmapGridConfig?.gridCols) >= 1) : previewType === 'map' ? !((previewMapConfig?.latColumn && previewMapConfig?.lngColumn) || previewMapConfig?.addressColumn) : (!previewXColumn || !previewYColumn)}
                        sx={{ ml: 1 }}
                      >
                        Guardar cambios
                      </Button>
                    )}
                  </>
                )}
              </Box>

              {queryResult && !compareResults ? (
                (missingRequiredPreviewFilters.length > 0) ? (
                  <Paper sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: 'action.hover' }}>
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 360 }}>
                      Selecciona valor de previsualización para {missingRequiredPreviewFilters.map((f) => `@${String(f.paramName).trim()}`).join(', ')} en <strong>Propiedades del gráfico</strong> para ejecutar y ver resultados.
                    </Typography>
                  </Paper>
                ) : (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <ChartEditDialog
                    embedded
                    value={chartValueForPreview}
                    onChange={(cfg) => {
                      setPreviewType(cfg.type ?? 'line');
                      setPreviewXColumn(cfg.xColumn ?? '');
                      setPreviewYColumn(cfg.yColumn ?? '');
                      setPreviewGroupByColumn(cfg.groupByColumn ?? '');
                      setPreviewLabelColumn(cfg.labelColumn ?? '');
                      setPreviewStacked(cfg.stacked ?? false);
                      setPreviewAdvancedConfig(cfg.advancedConfig ?? previewAdvancedConfig);
                      setPreviewHeatmapGridConfig(cfg.heatmapGridConfig ?? { gridRows: 3, gridCols: 3, showValues: true, valueColumn: '', filterColumn: '', cellAssignments: {} });
                      setPreviewMapConfig(cfg.mapConfig ?? { latColumn: '', lngColumn: '', addressColumn: '', comunaColumn: '', regionColumn: '', countryColumn: '', labelColumn: '', valueColumn: '', valueMapping: 'size', valueColorMin: '#2196f3', valueColorMax: '#f44336', popupColumns: [], markerStyle: 'circle', markerColor: '#1976d2', markerSize: 'medium' });
                    }}
                    queryData={queryResult}
                    availableColumns={getAvailableColumns()}
                    currentQuery={query}
                    onReplaceQueryLiteral={handleReplaceQueryLiteral}
                    dynamicFilterOptionSources={dynamicFilterOptionSources}
                    queryDynamicFilterConfig={queryDynamicFilterConfig}
                    getAuthHeaders={getAuthHeaders}
                    currentDatabase={currentDatabase}
                  />
                </Box>
                )
              ) : queryResult && compareResults && previousQueryResult ? (
                <Paper sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }}>
                  <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#858585', mb: 1, display: 'block' }}>
                        Resultado Anterior
                      </Typography>
                      <DataTable data={previousQueryResult.data || []} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#858585', mb: 1, display: 'block' }}>
                        Resultado Actual
                      </Typography>
                      <DataTable data={queryResult.data || []} />
                    </Box>
                  </Box>
                </Paper>
              ) : !queryResult ? (
                <Paper sx={{ flex: 1, overflow: 'auto', p: 2, position: 'relative' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 360 }}>
                      {activeDynamicFiltersForPreview.length > 0
                        ? <>Tienes {activeDynamicFiltersForPreview.length} filtro(s) dinámico(s) activo(s). Configura valores en <strong>Propiedades del gráfico</strong> y pulsa Ejecutar Query.</>
                        : 'Ejecuta una query para ver los resultados aquí'}
                    </Typography>
                    {activeDynamicFiltersForPreview.length > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => executeQuery()}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                      >
                        {loading ? 'Ejecutando...' : 'Ejecutar Query'}
                      </Button>
                    )}
                  </Box>
                </Paper>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <ChartEditDialog
                    embedded
                    value={chartValueForPreview}
                    onChange={(cfg) => {
                      setPreviewType(cfg.type ?? 'line');
                      setPreviewXColumn(cfg.xColumn ?? '');
                      setPreviewYColumn(cfg.yColumn ?? '');
                      setPreviewGroupByColumn(cfg.groupByColumn ?? '');
                      setPreviewLabelColumn(cfg.labelColumn ?? '');
                      setPreviewStacked(cfg.stacked ?? false);
                      setPreviewAdvancedConfig(cfg.advancedConfig ?? previewAdvancedConfig);
                      setPreviewHeatmapGridConfig(cfg.heatmapGridConfig ?? { gridRows: 3, gridCols: 3, showValues: true, valueColumn: '', filterColumn: '', cellAssignments: {} });
                      setPreviewMapConfig(cfg.mapConfig ?? { latColumn: '', lngColumn: '', addressColumn: '', comunaColumn: '', regionColumn: '', countryColumn: '', labelColumn: '', valueColumn: '', valueMapping: 'size', valueColorMin: '#2196f3', valueColorMax: '#f44336', popupColumns: [], markerStyle: 'circle', markerColor: '#1976d2', markerSize: 'medium' });
                    }}
                    queryData={queryResult}
                    availableColumns={getAvailableColumns()}
                    currentQuery={query}
                    onReplaceQueryLiteral={handleReplaceQueryLiteral}
                    dynamicFilterOptionSources={dynamicFilterOptionSources}
                    queryDynamicFilterConfig={queryDynamicFilterConfig}
                    getAuthHeaders={getAuthHeaders}
                    currentDatabase={currentDatabase}
                  />
                </Box>
              )}
              </Box>
            ) : (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary" align="center">
                  Escribe y ejecuta una query para ver los resultados aquí
                </Typography>
              </Box>
            )}
          </Panel>
        </PanelGroup>
      </Box>

      {/* Menu contextual para queries */}
      {menuAnchor && (
        <Menu
          anchorEl={menuAnchor.el}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleRenameQuery(menuAnchor.queryId)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Renombrar
          </MenuItem>
          <MenuItem onClick={(e) => handleDeleteQuery(menuAnchor.queryId, e)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Eliminar
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};

export default QueryEditor;
